/**
 * Slider Block
 * Self-contained slider component that can be used in any section.
 * Children of the slider block become slides.
 */

// Track initialized sliders to prevent duplicates
const initializedSliders = new WeakSet();

// Queue for sliders waiting for Swiper to load
const pendingInitializations = [];

// Store instances for resize handling
const sliderInstances = new Map();

// Breakpoint for mobile/desktop (matches Tailwind md:)
const MOBILE_BREAKPOINT = 768;

/**
 * Largest numeric slidesPerView from Swiper options (mobile + breakpoints).
 * Returns null if only "auto" / non-numeric values — caller skips loop heuristics.
 */
function getMaxNumericSlidesPerView(options) {
  if (!options || typeof options !== 'object') return null;
  let max = null;
  const consider = (v) => {
    if (typeof v === 'number' && v > 0 && !Number.isNaN(v)) {
      max = max === null ? v : Math.max(max, v);
    }
  };
  consider(options.slidesPerView);
  if (options.breakpoints && typeof options.breakpoints === 'object') {
    Object.values(options.breakpoints).forEach((bp) => {
      if (bp && bp.slidesPerView !== undefined) consider(bp.slidesPerView);
    });
  }
  return max;
}

/**
 * Swiper loop duplicates slides; it breaks (and spams console) when
 * slideCount <= slidesPerView at any breakpoint. Use rewind instead for smooth wrap.
 * Theme JSON only includes "loop":true when enabled (omit when off) — treat missing as false.
 */
/**
 * Liquid often emits `"pagination":false` when pagination is off. Swiper merges that
 * over module defaults and can leave params in a bad state; omit the key instead.
 */
function stripDisabledSwiperModules(options) {
  if (!options || typeof options !== 'object') return;
  ['pagination', 'navigation', 'scrollbar'].forEach((key) => {
    if (options[key] === false) delete options[key];
  });
}

/** Swiper 11+ requires autoplay.enabled — theme JSON only passes delay. */
function normalizeAutoplayEnabled(options) {
  if (!options || typeof options !== 'object') return;
  if (options.autoplay && typeof options.autoplay === 'object') {
    options.autoplay.enabled = true;
  }
}

/** Opt-in: ?sliderDebug=1 or localStorage theme:sliderDebug = 1 */
function isSliderDebugEnabled() {
  try {
    if (typeof window !== 'undefined' && window.location?.search?.includes('sliderDebug=1')) return true;
    if (typeof localStorage !== 'undefined' && localStorage.getItem('theme:sliderDebug') === '1') return true;
  } catch (_) {}
  return false;
}

function sliderDebugLog(label, payload) {
  if (!isSliderDebugEnabled()) return;
  console.info(`[SliderBlock] ${label}`, payload);
}

/**
 * When slideCount <= slidesPerView at a breakpoint, Swiper only has one snap — there is
 * nothing to scroll, so arrows and autoplay look broken (slideTo(0) is a no-op at index 0).
 * Lower numeric slidesPerView to slideCount - 1 (minimum 1). Skips slidesPerView "auto".
 */
function clampSlidesPerViewWhenAllSlidesFit(swiper, slideCount) {
  if (!swiper || slideCount < 2) return;

  const clampValue = (spv) => {
    if (typeof spv !== 'number' || !Number.isFinite(spv) || spv <= 0) return null;
    if (slideCount > spv) return null;
    return Math.max(1, slideCount - 1);
  };

  let changed = false;
  const applyToParams = (params) => {
    if (!params || typeof params !== 'object') return;
    if (params.slidesPerView !== 'auto') {
      const next = clampValue(params.slidesPerView);
      if (next !== null && next !== params.slidesPerView) {
        params.slidesPerView = next;
        changed = true;
      }
    }
    if (params.breakpoints && typeof params.breakpoints === 'object') {
      Object.keys(params.breakpoints).forEach((key) => {
        const bp = params.breakpoints[key];
        if (!bp || bp.slidesPerView === 'auto') return;
        const next = clampValue(bp.slidesPerView);
        if (next !== null && next !== bp.slidesPerView) {
          bp.slidesPerView = next;
          changed = true;
        }
      });
    }
  };

  applyToParams(swiper.params);
  if (swiper.originalParams) applyToParams(swiper.originalParams);

  if (changed) {
    swiper.setBreakpoint();
    swiper.update();
  }
}

/**
 * Theme editor preview uses a narrow iframe; `window.innerWidth` is the iframe, not the merchant's
 * browser. Use the parent window in design mode so desktop alignment / centering match the sidebar.
 */
function getEffectiveViewportWidth() {
  if (typeof window === 'undefined') return MOBILE_BREAKPOINT;
  try {
    if (window.Shopify?.designMode && window.parent && window.parent !== window) {
      return window.parent.innerWidth || window.innerWidth;
    }
  } catch (_) {
    /* cross-origin parent */
  }
  return window.innerWidth;
}

/**
 * Fast filters horizontal alignment (mobile vs desktop) from Swiper root data attributes.
 */
function getFastFiltersEffectiveAlign(swiperEl) {
  if (!swiperEl) return 'flex-start';
  const useDesktop = getEffectiveViewportWidth() >= MOBILE_BREAKPOINT;
  const raw = useDesktop
    ? swiperEl.dataset.ffSwiperAlignDesktop
    : swiperEl.dataset.ffSwiperAlignMobile;
  return (raw || 'flex-start').trim();
}

/** True when chip total width (incl. spaceBetween) is narrower than the swiper track. */
function fastFiltersRowFitsTrack(swiper) {
  const { slidesSizesGrid, params } = swiper;
  if (!slidesSizesGrid?.length) return false;
  const sb = Number(params.spaceBetween) || 0;
  let total = 0;
  slidesSizesGrid.forEach((size, i) => {
    total += size;
    if (i < slidesSizesGrid.length - 1) total += sb;
  });
  const track = swiper.isHorizontal() ? swiper.size : swiper.height;
  return track > 0 && total < track - 0.5;
}

/**
 * When the row fits (`isLocked` or measured narrower than the track), translate stays at 0 — safe to
 * mirror block alignment with `justify-content` on `.swiper-wrapper`. When the row overflows,
 * clear it so Swiper’s `translate3d` is not fighting flex justification while dragging.
 */
function applyFastFiltersWrapperJustify(swiper) {
  const root = swiper?.el;
  if (!root || root.dataset.ffSwiperFastFilters !== '1') return;
  const wrapper = root.querySelector('.swiper-wrapper');
  if (!wrapper) return;
  const align = getFastFiltersEffectiveAlign(root);
  const map = {
    'flex-start': 'flex-start',
    center: 'center',
    'flex-end': 'flex-end',
    'space-between': 'space-between',
    'space-around': 'space-around',
    'space-evenly': 'space-evenly',
  };
  const jc = map[align] || 'flex-start';
  const staticRow = swiper.isLocked || fastFiltersRowFitsTrack(swiper);
  if (staticRow) {
    wrapper.style.justifyContent = jc;
  } else {
    wrapper.style.justifyContent = '';
  }
}

function syncFastFiltersLayout(swiper) {
  if (swiper.el?.dataset?.ffSwiperFastFilters !== '1') return;
  requestAnimationFrame(() => {
    try {
      swiper.update();
    } catch (_) {}
    applyFastFiltersWrapperJustify(swiper);
  });
}

function applyLoopRewindForSlideCount(slides, customOptions) {
  const themeRequestedLoop = customOptions.loop === true;
  const n = slides.length;

  if (n < 2) {
    customOptions.loop = false;
    delete customOptions.rewind;
    return;
  }

  if (!themeRequestedLoop) {
    customOptions.loop = false;
    delete customOptions.rewind;
    return;
  }

  const maxSpv = getMaxNumericSlidesPerView(customOptions);
  let useLoop = false;

  if (maxSpv === null) {
    // "auto" or unknown — need enough slides for loop clones
    useLoop = n >= 4;
  } else {
    useLoop = n > maxSpv;
  }

  customOptions.loop = useLoop;

  if (!useLoop) {
    customOptions.rewind = true;
  } else {
    delete customOptions.rewind;
  }
}

class SliderBlock {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.swiperEl = null;
    this.contentEl = null;
    this.originalSlides = null;
    /** When set, slides were taken from inside this container (e.g. fast-filters); destroy() nests them back. */
    this.slideGroupContainer = null;
    /** Slides that belonged to `slideGroupContainer` before Swiper (for restore order with sibling blocks). */
    this.ffInnerSlides = null;
    /** Top-level siblings before the slide group in `.slider-content` (preserve order on destroy). */
    this.ffRestoreBefore = null;
    this.ffRestoreAfter = null;
    this.isInitialized = false;
    // Recently viewed blocks inject slides and call Swiper from recently-viewed.js.
    // If we run later (e.g. on resize), we would wrap their swiper markup and break it.
    if (element.closest('recently-viewed')) return;

    // Prevent duplicate instance creation
    if (initializedSliders.has(element)) return;
    initializedSliders.add(element);

    // Store visibility setting
    this.visibility = this.element.dataset.sliderVisibility || 'both';

    // Store instance for resize handling
    sliderInstances.set(element, this);

    // Check if should init based on visibility
    this.checkVisibilityAndInit();

    // Listen for resize
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Check if slider should be active based on visibility setting and viewport
   */
  shouldBeActive() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    
    switch (this.visibility) {
      case 'mobile':
        return isMobile;
      case 'desktop':
        return !isMobile;
      case 'both':
      default:
        return true;
    }
  }

  /**
   * Check visibility and init/destroy accordingly
   */
  checkVisibilityAndInit() {
    const shouldBeActive = this.shouldBeActive();

    if (shouldBeActive && !this.isInitialized) {
      this.init();
    } else if (!shouldBeActive && this.isInitialized) {
      this.destroy();
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.checkVisibilityAndInit();
    }, 150);
  }

  /**
   * Initializes the Swiper slider
   */
  init() {
    const Swiper = window.Swiper;

    if (!Swiper) {
      pendingInitializations.push(this);
      return;
    }

    // Content container
    this.contentEl = this.element.querySelector('.slider-content');
    if (!this.contentEl) return;

    // Find and preserve control elements before clearing
    // First try within the slider (nested), then try in parent section (separate blocks)
    const blockId = this.element.dataset.blockId;
    let controlsElement = this.element.querySelector('[data-slider-control]');
    
    if (!controlsElement && blockId) {
      // Look for controls in the same section that target this slider
      const section = this.element.closest('[id^="shopify-section-"]');
      if (section) {
        controlsElement = section.querySelector(`[data-slider-control][data-slider-id="${blockId}"]`);
      }
      
      // If still not found, try any controls without a specific slider ID in the section
      if (!controlsElement) {
        const allControls = section?.querySelectorAll('[data-slider-control]:not([data-slider-id])');
        if (allControls && allControls.length === 1) {
          // Only auto-match if there's exactly one slider and one unassigned controls block
          const allSliders = section?.querySelectorAll('[data-slider-block]');
          if (allSliders && allSliders.length === 1) {
            controlsElement = allControls[0];
          }
        }
      }
    }

    let slides;
    
    if (this.originalSlides) {
      slides = this.originalSlides;
    } else {
      this.slideGroupContainer = null;
      this.ffInnerSlides = null;
      this.ffRestoreBefore = null;
      this.ffRestoreAfter = null;
      // Direct children become slides (filter out style, script, controls, and other non-content elements)
      const excludedTags = ['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'];
      slides = [...this.contentEl.children].filter(el => 
        !excludedTags.includes(el.tagName) && 
        !el.hasAttribute('data-slider-control')
      );
      
      if (slides.length < 1) {
        return;
      }

      // Fast filters: expand `data-slider-slide-group` so each chip is a slide (even if other blocks share the slider).
      const groupIndex = slides.findIndex((el) => el.hasAttribute('data-slider-slide-group'));
      if (groupIndex !== -1) {
        const groupEl = slides[groupIndex];
        const innerSlides = [...groupEl.children].filter(
          (el) =>
            el.matches?.('[data-slider-slide-item]') &&
            !excludedTags.includes(el.tagName)
        );
        if (innerSlides.length > 0) {
          this.slideGroupContainer = groupEl;
          this.ffInnerSlides = innerSlides;
          this.ffRestoreBefore = slides.slice(0, groupIndex);
          this.ffRestoreAfter = slides.slice(groupIndex + 1);
          slides = [...this.ffRestoreBefore, ...innerSlides, ...this.ffRestoreAfter];
        }
      }

      this.originalSlides = slides;
    }

    /** Only fast-filters uses `data-slider-slide-group` today; keep nested/touch fixes scoped to that. */
    const isFastFiltersSlideGroup =
      !!this.slideGroupContainer?.classList?.contains('fast-filters');

    const ffGroup = this.slideGroupContainer;
    const readFfAttr = (name) =>
      typeof ffGroup?.getAttribute === 'function' ? (ffGroup.getAttribute(name) || '').trim() : '';
    const ffLegacy = readFfAttr('data-ff-align');
    const ffAlignMobile = readFfAttr('data-ff-align-mobile') || ffLegacy;
    const ffAlignDesktop = readFfAttr('data-ff-align-desktop') || ffLegacy;
    const ffGapStr = readFfAttr('data-ff-space-between');
    let ffSpaceBetweenPx = null;
    if (ffGapStr !== '') {
      const n = Number.parseInt(ffGapStr, 10);
      if (Number.isFinite(n) && n >= 0) ffSpaceBetweenPx = n;
    }

    // Create Swiper markup
    const swiperEl = document.createElement('div');
    swiperEl.classList.add('slider-block-swiper', 'w-full', 'max-w-full');
    
    if (this.element.dataset.overflowHidden !== 'false') {
      swiperEl.classList.add('overflow-x-hidden', 'overflow-y-hidden');
    }
    
    swiperEl.dataset.swiperInstance = this.element.dataset.blockId;
    if (ffAlignMobile) {
      swiperEl.dataset.ffSwiperAlignMobile = ffAlignMobile;
    }
    if (ffAlignDesktop) {
      swiperEl.dataset.ffSwiperAlignDesktop = ffAlignDesktop;
    }
    if (isFastFiltersSlideGroup) {
      swiperEl.dataset.ffSwiperFastFilters = '1';
    }
    this.swiperEl = swiperEl;

    const wrapperEl = document.createElement('div');
    wrapperEl.classList.add('swiper-wrapper');

    // Move children into Swiper wrapper as slides
    slides.forEach(slide => {
      slide.classList.add('swiper-slide');
      wrapperEl.appendChild(slide);
    });

    swiperEl.appendChild(wrapperEl);

    // Replace content with Swiper
    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(swiperEl);
    
    // Re-append controls if they were nested inside the slider
    // (If controls are outside in the section, they stay where they are)
    const wasNested = controlsElement?.parentElement === this.contentEl;
    if (controlsElement && wasNested) {
      this.element.appendChild(controlsElement);
    }

    // Get options from data attribute
    let customOptions = {};
    if (this.element.dataset.swiperOptions) {
      try {
        customOptions = JSON.parse(this.element.dataset.swiperOptions);
      } catch (err) {
        console.warn('[SliderBlock] Invalid data-swiper-options JSON', err);
      }
    }
    stripDisabledSwiperModules(customOptions);
    normalizeAutoplayEnabled(customOptions);

    // Parent Slider + nested Fast filters: both are horizontal Swipers — without this, drags on chips
    // move the parent's .swiper-wrapper (whitespace grows). Parent ignores touches inside .fast-filters-host.
    const slideHasNestedFastFiltersHost = slides.some(
      (el) =>
        el?.classList?.contains('fast-filters-host') ||
        !!el?.querySelector?.('.fast-filters-host')
    );
    if (slideHasNestedFastFiltersHost) {
      const ffHostSel = '.fast-filters-host';
      const existing = customOptions.noSwipingSelector
        ? String(customOptions.noSwipingSelector)
        : '';
      if (!existing.includes('fast-filters-host')) {
        customOptions.noSwipingSelector = existing ? `${existing}, ${ffHostSel}` : ffHostSel;
      }
    }

    // Default options
    const defaultOptions = {
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 16,
      speed: 450,
      // Default Swiper watchOverflow sets allowSlideNext/Prev false when snapGrid has one step
      // ("all slides fit"), which blocks slideTo() — arrow nav and rewind do nothing.
      watchOverflow: false,
      // Enable touch/drag interactions
      allowTouchMove: true,
      simulateTouch: true,
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      // Improve touch responsiveness
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      followFinger: true,
      threshold: 5,
      touchMoveStopPropagation: false,
      touchStartPreventDefault: false,
      touchStartForcePreventDefault: false,
      touchReleaseOnEdges: false,
    };

    const customPaginationColour = this.element.dataset.paginationColour;

    // Loop requires more slides than fit in the viewport; otherwise disable loop and use rewind (smooth wrap).
    applyLoopRewindForSlideCount(slides, customOptions);

    // Fast filters: justify-content on .swiper-wrapper only when isLocked (see applyFastFiltersWrapperJustify).
    if (ffAlignMobile || ffAlignDesktop) {
      customOptions.centerInsufficientSlides = false;
      const prevOn = customOptions.on || {};
      customOptions.on = {
        ...prevOn,
        init(swiper) {
          prevOn.init?.(swiper);
          syncFastFiltersLayout(swiper);
        },
        resize(swiper) {
          prevOn.resize?.(swiper);
          syncFastFiltersLayout(swiper);
        },
        breakpoint(swiper, breakpointParams) {
          prevOn.breakpoint?.(swiper, breakpointParams);
          syncFastFiltersLayout(swiper);
        },
        update(swiper) {
          prevOn.update?.(swiper);
          applyFastFiltersWrapperJustify(swiper);
        },
        lock(swiper) {
          prevOn.lock?.(swiper);
          applyFastFiltersWrapperJustify(swiper);
        },
        unlock(swiper) {
          prevOn.unlock?.(swiper);
          applyFastFiltersWrapperJustify(swiper);
        },
        touchEnd(swiper) {
          prevOn.touchEnd?.(swiper);
          requestAnimationFrame(() => applyFastFiltersWrapperJustify(swiper));
        },
      };
    }
    // Space between buttons (fast-filters) overrides Slider spaceBetween so one setting matches the carousel.
    if (ffSpaceBetweenPx !== null) {
      customOptions.spaceBetween = ffSpaceBetweenPx;
    }

    // Inner fast-filters Swiper sits inside .fast-filters-host; parent may use noSwipingSelector for that host.
    // Disable noSwiping on the inner instance so closest(.fast-filters-host) does not block chip drags.
    if (isFastFiltersSlideGroup) {
      customOptions.noSwiping = false;
      customOptions.touchMoveStopPropagation = true;
    }

    // Navigation: always bind prev/next DOM nodes when present (avoids selector/ID mismatch edge cases)
    let navNextBtn = null;
    let navPrevBtn = null;
    if (customOptions.navigation !== false && controlsElement) {
      const nextBtn = controlsElement.querySelector('.slider-navigation-next');
      const prevBtn = controlsElement.querySelector('.slider-navigation-prev');
      if (nextBtn && prevBtn) {
        navNextBtn = nextBtn;
        navPrevBtn = prevBtn;
        // Swiper default lockClass is swiper-button-lock; bundled CSS hides it with display:none.
        // When all slides fit the viewport, isLocked is true — our custom nav would disappear.
        // Use a neutral class so arrows stay visible (loop / manual advance still work).
        customOptions.navigation = {
          nextEl: nextBtn,
          prevEl: prevBtn,
          lockClass: 'slider-navigation-lock',
        };
      }
    }

    // Check if slider-controls block is present (already found earlier)
    let controlsPaginationColour = customPaginationColour;
    
    if (controlsElement) {
      // Find pagination element
      const paginationEl = controlsElement.querySelector('.swiper-pagination');
      if (paginationEl) {
        // Get pagination type and colour from data attributes
        const paginationType = paginationEl.dataset.paginationType || 'bullets';
        controlsPaginationColour = paginationEl.dataset.paginationColour || customPaginationColour;
        
        // Override pagination config
        customOptions.pagination = {
          el: paginationEl,
          type: paginationType,
          clickable: true,
        };
        
        // Handle custom pagination type - render as styled bars
        if (paginationType === 'custom') {
          customOptions.pagination.renderCustom = (swiper, current, total) => {
            let bullets = '';
            for (let i = 1; i <= total; i++) {
              const isActive = i === current ? 'opacity-100' : 'opacity-50';
              bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${controlsPaginationColour};" data-slide-index="${i}"></span>`;
            }
            return `<div class="flex gap-2 justify-center">${bullets}</div>`;
          };

          // Make custom bullets clickable
          this.element.addEventListener('click', (e) => {
            const bullet = e.target.closest('[data-slide-index]');
            if (bullet && this.swiper) {
              const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
              this.swiper.slideTo(index);
            }
          });
        }
      }
    } else {
      // Fallback: Handle custom pagination type when no controls block (old behavior)
      if (customOptions.pagination && customOptions.pagination.type === 'custom') {
        customOptions.pagination.renderCustom = (swiper, current, total) => {
          let bullets = '';
          for (let i = 1; i <= total; i++) {
            const isActive = i === current ? 'opacity-100' : 'opacity-50';
            bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${customPaginationColour};" data-slide-index="${i}"></span>`;
          }
          return `<div class="flex gap-2 justify-center">${bullets}</div>`;
        };

        // Make custom bullets clickable
        this.element.addEventListener('click', (e) => {
          const bullet = e.target.closest('[data-slide-index]');
          if (bullet && this.swiper) {
            const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
            this.swiper.slideTo(index);
          }
        });
      }
    }

    // Initialize Swiper
    this.swiper = new Swiper(swiperEl, {
      ...defaultOptions,
      ...customOptions,
    });

    clampSlidesPerViewWhenAllSlidesFit(this.swiper, slides.length);

    if (this.swiper.el?.dataset?.ffSwiperFastFilters === '1') {
      syncFastFiltersLayout(this.swiper);
    }

    sliderDebugLog(this.element.id || 'slider', {
      blockId: this.element.dataset.blockId,
      controlsFound: Boolean(controlsElement),
      navBound: Boolean(navNextBtn && navPrevBtn),
      slideCount: slides.length,
      slidesPerView: this.swiper.params.slidesPerView,
      snapGridLength: this.swiper.snapGrid?.length,
      loop: this.swiper.params.loop,
      rewind: this.swiper.params.rewind,
      isLocked: this.swiper.isLocked,
      allowSlideNext: this.swiper.allowSlideNext,
      allowSlidePrev: this.swiper.allowSlidePrev,
    });

    // Swiper 11: autoplay sometimes needs an explicit start after init (theme editor / late layout).
    if (this.swiper.params.autoplay?.enabled && this.swiper.autoplay?.start) {
      requestAnimationFrame(() => {
        try {
          this.swiper.autoplay.start();
        } catch (_) {}
      });
    }

    this.isInitialized = true;
  }

  /**
   * Destroys the Swiper instance and restores original markup
   */
  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (this.originalSlides && this.contentEl) {
      this.contentEl.innerHTML = '';
      if (this.slideGroupContainer) {
        this.originalSlides.forEach((slide) => slide.classList.remove('swiper-slide'));
        (this.ffRestoreBefore || []).forEach((el) => this.contentEl.appendChild(el));
        (this.ffInnerSlides || []).forEach((slide) => this.slideGroupContainer.appendChild(slide));
        this.contentEl.appendChild(this.slideGroupContainer);
        (this.ffRestoreAfter || []).forEach((el) => this.contentEl.appendChild(el));
        this.slideGroupContainer = null;
        this.ffInnerSlides = null;
        this.ffRestoreBefore = null;
        this.ffRestoreAfter = null;
      } else {
        this.originalSlides.forEach((slide) => {
          slide.classList.remove('swiper-slide');
          this.contentEl.appendChild(slide);
        });
      }
    }

    this.originalSlides = null;
    this.swiperEl = null;
    this.isInitialized = false;
  }

  /**
   * Full cleanup including event listeners
   */
  cleanup() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    this.destroy();
  }
}

/**
 * Initialize all slider blocks on the page
 */
function initSliderBlocks() {
  const sliders = document.querySelectorAll('[data-slider-block]');
  sliders.forEach(slider => {
    new SliderBlock(slider);
  });
}

/**
 * Process pending initializations once Swiper is available
 */
function processPendingInitializations() {
  if (!window.Swiper || pendingInitializations.length === 0) return;
  
  while (pendingInitializations.length > 0) {
    const instance = pendingInitializations.shift();
    instance.init();
  }
}

/**
 * Wait for Swiper to be available
 */
function waitForSwiper() {
  if (window.Swiper) {
    processPendingInitializations();
    return;
  }

  let _swiper = window.Swiper;
  Object.defineProperty(window, 'Swiper', {
    configurable: true,
    get() {
      return _swiper;
    },
    set(value) {
      _swiper = value;
      Object.defineProperty(window, 'Swiper', {
        configurable: true,
        writable: true,
        value: value
      });
      processPendingInitializations();
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSliderBlocks();
    waitForSwiper();
  });
} else {
  initSliderBlocks();
  waitForSwiper();
}

// Re-initialize on back/forward navigation
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    initSliderBlocks();
  }
});

// Theme editor: new section HTML needs Swiper init (marquee.js no longer overwrites these exports)
document.addEventListener('shopify:section:load', () => {
  initSliderBlocks();
  processPendingInitializations();
});

// Export for potential external use
window.SliderBlock = SliderBlock;
window.initSliderBlocks = initSliderBlocks;
window.applyLoopRewindForSlideCount = applyLoopRewindForSlideCount;
window.clampSlidesPerViewWhenAllSlidesFit = clampSlidesPerViewWhenAllSlidesFit;
window.stripDisabledSwiperModules = stripDisabledSwiperModules;
window.normalizeAutoplayEnabled = normalizeAutoplayEnabled;

